import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { createCollection } from '@platform/client';
import { createCollectionSchema } from '@platform/schema/authoring';
import {
  COLLECTION_NOUN,
  difficultySchema,
  illustrationObjectSchema,
  subjectSchema,
} from '@platform/schema/content';
import { slugify } from '@platform/schema/generation';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { ChoiceField } from '../authoring/ChoiceField';
import { describeObject, groupOfObject } from '../authoring/illustration';
import { PageHeader, SCREEN_GAP } from '../routes/PageHeader';

import { subjectLabel } from './subject';

import type {
  Difficulty,
  IllustrationObject,
  Subject,
} from '@platform/schema/content';

/**
 * A collection made by hand — GEN-58 for courses, GEN-39 for paths.
 *
 * Not everything comes from a generation run: a course is the editorial container a run
 * fills, and a path is somebody's argument about what to learn in what order. Neither
 * could be made without SQL.
 *
 * One form over both kinds, and it asks for every column publishing needs — including
 * the two `CollectionHeader` cannot edit, without which a created path could never be
 * published from the console. `docs/decisions/library-screens.md` §"Making one by hand"
 * argues both, and why `MODULE` is not a third arm.
 */
export function NewCollection({ kind }: { kind: 'COURSE' | 'PATH' }) {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [subject, setSubject] = useState<Subject>('FINANCE');
  const [difficulty, setDifficulty] = useState<Difficulty>('BEGINNER');
  const [objectName, setObjectName] = useState<IllustrationObject>('balance');

  const noun = COLLECTION_NOUN[kind];

  /**
   * Trimmed, because only the slug was — a title typed with a stray leading space was
   * stored with it and then sorted under it.
   */
  const body = {
    kind,
    slug: slugify(title),
    title: title.trim(),
    summary: summary.trim(),
    subject,
    difficulty,
    objectName,
  };

  /**
   * The button asks the schema, rather than re-spelling its bounds — GEN-39.
   *
   * Written as `length >= 3` on both fields it agreed with the API at the short end and
   * not at the long one: a 300-character title left the button enabled and came back a
   * 400. A second copy of a bound is a copy that drifts, and this one had already.
   *
   * It also closes the case no length check would have: a title of punctuation or of a
   * non-Latin script derives an *empty* slug, which is three characters short of the
   * column's minimum and is not visible in the length of anything the operator typed.
   */
  const refused = createCollectionSchema.safeParse(body).error?.issues[0];

  const create = useTrackedMutation(`Create the ${noun}`, {
    mutationFn: async () => {
      const written = await createCollection({ body, throwOnError: true });

      return written.data.id;
    },
    onSuccess: (id: string) => {
      void navigate(`/library/${noun}s/${id}`);
    },
  });

  return (
    <Stack spacing={SCREEN_GAP} sx={{ maxWidth: 520 }}>
      <PageHeader title={`New ${noun}`} />

      <TextField
        label="Title"
        size="small"
        value={title}
        onChange={(event) => {
          setTitle(event.target.value);
        }}
      />

      <TextField
        label="Summary"
        size="small"
        multiline
        value={summary}
        onChange={(event) => {
          setSummary(event.target.value);
        }}
      />

      <ChoiceField
        label="Topic"
        value={subject}
        options={subjectSchema.options}
        describe={subjectLabel}
        onChange={setSubject}
      />

      <ChoiceField
        label="Level"
        value={difficulty}
        options={difficultySchema.options}
        onChange={setDifficulty}
      />

      {/* Through the shared helpers, so both screens name and group the objects
          identically — `authoring/illustration.ts` holds the reasoning. */}
      <ChoiceField
        label="Card illustration"
        value={objectName}
        options={illustrationObjectSchema.options}
        describe={describeObject}
        group={groupOfObject}
        onChange={setObjectName}
      />

      <Typography variant="caption" color="text.secondary">
        It will be filed as {body.slug === '' ? '—' : body.slug}
      </Typography>

      {/* Which field, and what is wrong with it. A disabled button that says nothing is
          the same dead end as the 400 it is standing in for. */}
      {title !== '' && refused !== undefined && (
        <Typography variant="caption" color="error">
          {refused.path.join('.')}: {refused.message}
        </Typography>
      )}

      <Stack direction="row">
        <Button
          variant="contained"
          disabled={refused !== undefined || create.isPending}
          onClick={() => {
            create.mutate();
          }}
        >
          Create the {noun}
        </Button>
      </Stack>
    </Stack>
  );
}
